/**
 * SuggestionCard Component - SPEC-019 Task 5.1
 *
 * Displays a single AI suggestion with score, reason, and feedback buttons.
 * Refactored to use CVA (Class Variance Authority) for variant management.
 *
 * @module features/ai-button/components/SuggestionCard
 * @see SPEC-019 Requirements 3.1, 3.2, 3.3, 3.6, 8.4, 8.5
 */

import { memo } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import type { CandidateKind, FeedbackAction, NormalizedFeatures } from '@linkode/shared';
import { cn } from '../../../lib/cn';
import { useTypedTranslation } from '../../../i18n';

// ============================================================================
// CVA Variants
// ============================================================================

/**
 * SuggestionCard variants using CVA (Class Variance Authority)
 * Defines kind, isSelected, and feedbackState variations
 */
export const suggestionCardVariants = cva(
  // Base styles
  ['flex items-start gap-2 p-3', 'bg-white border rounded-md', 'transition-all duration-200'],
  {
    variants: {
      kind: {
        backlink: ['border-blue-200'],
        tag: ['border-green-200'],
      },
      isSelected: {
        true: ['border-blue-500', 'bg-blue-50', 'ring-1', 'ring-blue-500'],
        false: ['border-gray-200', 'hover:border-gray-300'],
      },
      feedbackState: {
        none: [],
        up: ['bg-green-50'],
        down: ['bg-gray-100', 'opacity-60'],
      },
    },
    defaultVariants: {
      kind: 'backlink',
      isSelected: false,
      feedbackState: 'none',
    },
  }
);

/**
 * Kind badge variants
 */
export const kindBadgeVariants = cva(['px-1.5 py-0.5 text-xs font-medium rounded uppercase'], {
  variants: {
    kind: {
      backlink: ['bg-blue-100', 'text-blue-800'],
      tag: ['bg-green-100', 'text-green-800'],
    },
  },
  defaultVariants: {
    kind: 'backlink',
  },
});

/** Exported type for SuggestionCard variants */
export type SuggestionCardVariants = VariantProps<typeof suggestionCardVariants>;

// ============================================================================
// Types
// ============================================================================

export interface SuggestionCardProps extends SuggestionCardVariants {
  /** Unique identifier (docId for backlinks, tag string for tags) */
  candidateId: string;
  /** Type of suggestion */
  kind: CandidateKind;
  /** Display title (document title for backlinks, tag for tags) */
  title: string;
  /** Recommendation reason */
  reason: string;
  /** Final score (0-1) */
  score: number;
  /** Score breakdown */
  scoreBreakdown: NormalizedFeatures;
  /** Whether this card is selected */
  isSelected: boolean;
  /** Current feedback action if any */
  feedback?: FeedbackAction;
  /** Callback when selection is toggled */
  onToggleSelect: () => void;
  /** Callback when feedback is submitted */
  onFeedback: (action: FeedbackAction) => void;
  /** Tab index for keyboard navigation */
  tabIndex?: number;
  /** Callback when card receives focus */
  onFocus?: () => void;
}

// ============================================================================
// Component
// ============================================================================

/**
 * SuggestionCard Component
 *
 * Displays suggestion details with:
 * - Selection checkbox
 * - Title and reason
 * - Score visualization
 * - Thumbs up/down feedback buttons
 *
 * @see SPEC-019 Requirements 3.1, 3.2, 3.3, 3.6, 8.4, 8.5
 */
export const SuggestionCard = memo(function SuggestionCard({
  candidateId,
  kind,
  title,
  reason,
  score,
  scoreBreakdown,
  isSelected,
  feedback,
  onToggleSelect,
  onFeedback,
  tabIndex = 0,
  onFocus,
}: SuggestionCardProps) {
  const { t } = useTypedTranslation();

  const scorePercent = Math.round(score * 100);
  const kindLabel = kind === 'backlink' ? t('ai.suggestion.backlink') : t('ai.suggestion.tag');
  const feedbackState = feedback ?? 'none';

  return (
    <div
      className={cn(suggestionCardVariants({ kind, isSelected, feedbackState }))}
      data-testid={`suggestion-card-${candidateId}`}
      data-selected={isSelected ? 'true' : 'false'}
      role="article"
      aria-label={`${kindLabel}: ${title}`}
      tabIndex={tabIndex}
      onFocus={onFocus}
    >
      {/* Selection Checkbox */}
      <div className="flex-shrink-0 pt-0.5">
        <input
          type="checkbox"
          id={`select-${candidateId}`}
          checked={isSelected}
          onChange={onToggleSelect}
          aria-label={t('ai.suggestion.select', { title })}
          className="w-4 h-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-1">
          <span className={cn(kindBadgeVariants({ kind }))} data-kind={kind}>
            {kindLabel}
          </span>
          <h4 className="text-sm font-medium truncate" data-testid="card-title">
            {title}
          </h4>
        </div>

        {reason && <p className="text-xs text-gray-600 mb-2 line-clamp-2">{reason}</p>}

        {/* Score Display */}
        <div className="flex items-center gap-2" title={t('ai.suggestion.scoreDetails')}>
          <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 transition-all duration-300"
              style={{ width: `${scorePercent}%` }}
              role="meter"
              aria-valuenow={scorePercent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={t('ai.suggestion.score', { score: scorePercent })}
            />
          </div>
          <span
            className="text-xs font-medium text-gray-500 min-w-[2.5rem] text-right"
            data-testid="score-value"
          >
            {scorePercent}%
          </span>
        </div>

        {/* Score Breakdown (shown on hover via CSS) */}
        <div
          className="hidden group-hover:flex gap-2 mt-1 text-xs text-gray-500"
          aria-hidden="true"
        >
          <span>vec: {Math.round(scoreBreakdown.vec01 * 100)}%</span>
          <span>graph: {Math.round(scoreBreakdown.graph01 * 100)}%</span>
          <span>time: {Math.round(scoreBreakdown.time01 * 100)}%</span>
        </div>
      </div>

      {/* Feedback Buttons */}
      <div
        className="flex flex-col gap-1 flex-shrink-0"
        role="group"
        aria-label={t('ai.feedback.label')}
      >
        <button
          type="button"
          data-testid="feedback-up"
          data-active={feedback === 'up' ? 'true' : 'false'}
          className={cn(
            'p-1 text-base rounded border transition-colors',
            feedback === 'up'
              ? 'bg-blue-100 border-blue-500'
              : 'bg-transparent border-gray-200 hover:bg-gray-100',
            feedback && 'opacity-60 cursor-not-allowed'
          )}
          onClick={() => onFeedback('up')}
          disabled={!!feedback}
          aria-label={t('ai.feedback.helpful')}
          aria-pressed={feedback === 'up'}
        >
          👍
        </button>
        <button
          type="button"
          data-testid="feedback-down"
          data-active={feedback === 'down' ? 'true' : 'false'}
          className={cn(
            'p-1 text-base rounded border transition-colors',
            feedback === 'down'
              ? 'bg-gray-200 border-gray-400'
              : 'bg-transparent border-gray-200 hover:bg-gray-100',
            feedback && 'opacity-60 cursor-not-allowed'
          )}
          onClick={() => onFeedback('down')}
          disabled={!!feedback}
          aria-label={t('ai.feedback.notHelpful')}
          aria-pressed={feedback === 'down'}
        >
          👎
        </button>
      </div>
    </div>
  );
});
